import React, { Suspense } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';

import Spinner from '../components/Spinner';
import ErrorBoundary from '../components/ErrorBoundary';
import lazyWithRetry from '../helpers/lazyWithRetry';
import { TransferDetailsContextProvider } from '../context/transfer-details.context';
import { BateDetailsContextProvider } from '../context/bate-details.context';
import { AccountDetailsContextProvider } from '../context/account-details.context';
import { RegistrationDetailsContextProvider } from '../context/registration-details.context';
import { GenericProcedureDetailsContextProvider } from '../context/generic-procedure-details.context';

const Layout = lazyWithRetry(() => import('../layout'));
const Accounts = lazyWithRetry(() => import('../screens/Backoffice/accounts'));
const CarDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/cars/details'),
);
const CustomerDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/customers/details'),
);
const Transfers = lazyWithRetry(() =>
  import('../screens/Backoffice/transfers'),
);
const TransferDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/transfers/details'),
);
const Reports = lazyWithRetry(() => import('../screens/Backoffice/reports'));
const Search = lazyWithRetry(() => import('../screens/Backoffice/search'));
const Dashboard = lazyWithRetry(() =>
  import('../screens/Backoffice/dashboard'),
);
const CompanyDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/companies/details'),
);
const Tools = lazyWithRetry(() => import('../screens/Backoffice/tools'));
const AccountDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/accounts/details'),
);
const PricePlans = lazyWithRetry(() =>
  import('../screens/Backoffice/price-plans'),
);
const Batecoms = lazyWithRetry(() => import('../screens/Backoffice/batecoms'));
const BateDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/bates/details'),
);
const Registrations = lazyWithRetry(() =>
  import('../screens/Backoffice/registrations'),
);
const RegistrationDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/registrations/details'),
);
const DgtFees = lazyWithRetry(() => import('../screens/Backoffice/dgt-fees'));
const B2CList = lazyWithRetry(() => import('../screens/Backoffice/b2c'));
const B2BList = lazyWithRetry(() => import('../screens/Backoffice/b2b'));
const Planning = lazyWithRetry(() => import('../screens/Backoffice/planning'));
const Shipments = lazyWithRetry(() =>
  import('../screens/Backoffice/shipments'),
);
const GenericProcedures = lazyWithRetry(() =>
  import('../screens/Backoffice/generic-procedures'),
);
const GenericProcedureDetails = lazyWithRetry(() =>
  import('../screens/Backoffice/generic-procedures/details'),
);

function BackofficeRouter() {
  return (
    <ErrorBoundary>
      <Layout>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-full">
              <Spinner color="text-blue-700" size={10} />
            </div>
          }
        >
          <Routes>
            <Route exact path="/" element={<Dashboard />} />
            <Route exact path="/search/:type/:query" element={<Search />} />
            <Route exact path="/search/:type/" element={<Search />} />
            <Route path="/customers/:userId" element={<CustomerDetails />} />
            <Route path="/vehicles/:carId" element={<CarDetails />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route
              path="/transfers/:code"
              element={
                <TransferDetailsContextProvider>
                  <TransferDetails />
                </TransferDetailsContextProvider>
              }
            />
            <Route path="/registrations" element={<Registrations />} />
            <Route
              path="/registration/:code"
              element={
                <RegistrationDetailsContextProvider>
                  <RegistrationDetails />
                </RegistrationDetailsContextProvider>
              }
            />
            <Route path="/batecoms" element={<Batecoms />} />
            <Route
              path="/bates/:code"
              element={
                <BateDetailsContextProvider>
                  <BateDetails />
                </BateDetailsContextProvider>
              }
            />
            <Route
              path="/account/:accountId"
              element={
                <AccountDetailsContextProvider>
                  <AccountDetails />
                </AccountDetailsContextProvider>
              }
            />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/reports-pro" element={<Reports isPro />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/pro-transfers" element={<Transfers isPro />} />
            <Route path="/companies/:companyId" element={<CompanyDetails />} />
            <Route path="/dgt-fees" element={<DgtFees />} />
            <Route path="/shipments" element={<Shipments />} />
            <Route path="/planning" element={<Planning />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/price-plans" element={<PricePlans />} />
            <Route path="/b2c/to-review" element={<B2CList />} />
            <Route path="/b2b/to-review" element={<B2BList />} />
            <Route
              path="/generic-procedures/:kind"
              element={<GenericProcedures />}
            />
            <Route
              path="/generic-procedure/:code"
              element={
                <GenericProcedureDetailsContextProvider>
                  <GenericProcedureDetails />
                </GenericProcedureDetailsContextProvider>
              }
            />
          </Routes>
        </Suspense>
      </Layout>
    </ErrorBoundary>
  );
}

export default function Backoffice() {
  return (
    <BrowserRouter>
      <BackofficeRouter />
    </BrowserRouter>
  );
}
